import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AppGlobals } from 'src/app/app.globals';
import { ProjectTaskService } from 'src/app/services/project-task.service';
import { ContractService } from 'src/app/services/contracts/contract.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { ConfigService } from 'src/app/services/config.service';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { GpdPillComponent } from 'src/app/components/gpd-pill/gpd-pill.component';
import { ProjectTaskListItem } from '../project-tasks/project-tasks.types';
import { TaskTypeEnum } from 'src/app/pages/config/project-config/project-structure-config/project-task-config/datatypes-taskconfig';
import { CalendarDay, CalendarEntry, CalendarEntryType } from '../../../../../components/calendar-month-panel/calendar.types';
import { CalendarMonthPanelComponent } from '../../../../../components/calendar-month-panel/calendar-month-panel.component';
import { CalendarDayAgendaPanelComponent } from '../../../../../components/calendar-day-agenda-panel/calendar-day-agenda-panel.component';
import { TaskDetailPanelComponent } from './task-detail-panel/task-detail-panel.component';
import { DeadlineDetailPanelComponent } from '../../../../../components/deadline-detail-panel/deadline-detail-panel.component';
import { FeatureFlags } from 'src/app/interfaces/enums/feature-flag';

// Ticket #4484 "Projects | Better calendar": replaces the previous custom-appointment
// scheduler with a read-only view of task deadlines (#4545) and contract deadlines (#4546),
// shown as a month grid plus a chronological list (#4547), filterable by type (#4548).
// Clicking an entry opens a detail side panel (#4549) with a link to the full record (#4550).
import { GpdFilter } from 'src/app/components/gpd-filter/gpd-filter';
import { TaskTagsComponent } from 'src/app/components/task-tags/task-tags.component';
import { TaskTag } from 'src/app/interfaces/entities/task-tag';

@Component({
    selector: 'app-project-calendar',
    imports: [CommonModule, GpdButtonComponent, GpdPillComponent, GpdFilter, TaskTagsComponent, CalendarMonthPanelComponent, CalendarDayAgendaPanelComponent, TaskDetailPanelComponent, DeadlineDetailPanelComponent],
    templateUrl: './project-calendar.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['../../../../../components/calendar-day-agenda-panel/entry-row.css', './project-calendar.component.css']
})
export class ProjectCalendarComponent implements OnInit {

  projectId: number;
  siteId: number | null = null;

  /** The entry currently shown in the detail side panel, if any. */
  selectedEntry: CalendarEntry | null = null;

  /** The day whose full agenda is currently shown in the side panel (opened from a day cell's
   *  "+N" indicator), if any. */
  selectedOverflowDay: CalendarDay | null = null;

  /** First-of-month anchor for whichever month is currently displayed. */
  viewDate: Date = this.firstOfMonth(new Date());

  allEntries: CalendarEntry[] = [];
  filterType: CalendarEntryType | 'all' = 'all';

  /** Tag filter (#4811): tags present on this project's dated tasks. No selection = no filtering;
   *  a selection keeps only task entries carrying one of the tags (deadlines have no tags). */
  tagOptions: TaskTag[] = [];
  selectedTagIds: number[] = [];

  /** Contracts are feature-gated server-side (ContractServiceController carries
   *  [FeatureGate(FeatureFlag.Contracts)]), so with the flag off every contract endpoint 404s.
   *  ContractService turns those rejections into AppGlobals.ShowErrorMessage(), which meant a
   *  tenant without contracts got error popups just for opening the calendar (#4484). Skip the
   *  contract calls instead - tasks are the only entry type such a tenant can have anyway.
   *  Read directly rather than via $features: the router-outlet only renders once app.component
   *  has set isLoggedIn, which happens after SetFeatures(), so the map is always populated here. */
  readonly contractsEnabled = AppGlobals.UserCan(FeatureFlags.Contracts);

  readonly locale = 'de-DE';

  readonly labelAll = $localize`:@@calendarFilterAll:All`;
  readonly labelTask = $localize`:@@task:Task`;
  readonly labelDeadline = $localize`:@@contractDeadlineLabel:Contract deadline`;
  readonly labelEntriesCount = $localize`:@@calendarEntriesCount:Entries`;

  constructor(
    private appGlobals: AppGlobals,
    private projectTaskService: ProjectTaskService,
    private contractService: ContractService,
    private utilitiesService: UtilitiesService,
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.appGlobals.SetGlobalMenuIndex(2);
    this.projectId = Number(this.activatedRoute.snapshot.params.id);
    // Restores whichever month was last viewed before navigating away (e.g. via "Open task" /
    // "Open contract") — without this, coming back re-runs the viewDate field initializer above
    // and silently jumps back to the current month instead of where the user left off.
    this.viewDate = this.restoreViewDate();
    this.loadEntries();

    // Needed by the detail panels: deadline type / contract type labels, and the site id
    // for building the "open task" route.
    if (this.contractsEnabled) {
      this.contractService.getContractTypes({});
      this.contractService.getContractDeadlineTypes({});
    }
    this.utilitiesService.getSiteIdByProjectId({ ProjectId: this.projectId }).then((result) => {
      this.siteId = result?.siteId ?? null;
    });
    // Kicks off early (this hits an external Heimdall identity service, so it's the slowest
    // of these calls) so it's already cached by the time a detail panel needs to resolve an
    // assignee's email to a display name, instead of the panel waiting on it cold.
    this.configService.loadUserList();
  }

  async loadEntries(): Promise<void> {
    const [taskResult, deadlines] = await Promise.all([
      this.projectTaskService.getProjektAufgaben({ WIProjectItem: { ProjectId: this.projectId } }),
      this.contractsEnabled ? this.contractService.getDeadlinesForProject(this.projectId) : Promise.resolve([]),
    ]);

    let taskEntries: CalendarEntry[] = [];
    if (taskResult?.hasError === true) {
      AppGlobals.ShowErrorMessage(taskResult.feedback);
    } else {
      const tasks: ProjectTaskListItem[] = taskResult?.projectTasks ?? [];
      taskEntries = tasks
        .filter((task) => task.type === TaskTypeEnum.Date && !!task.taskResult)
        .map((task): CalendarEntry | null => {
          const date = this.parseDateOnly(task.taskResult);
          if (!date) return null;
          const entry: CalendarEntry = { id: `task-${task.id}`, type: 'task', date, title: task.title, source: task, chipClass: 'entry-type-task', chipLabel: '' };
          entry.chipLabel = this.entryTypeLabel(entry);
          return entry;
        })
        .filter((entry): entry is CalendarEntry => entry !== null);
    }

    const deadlineEntries: CalendarEntry[] = (deadlines ?? [])
      .map((deadline: any): CalendarEntry | null => {
        const date = this.parseDateOnly(deadline.date);
        if (!date) return null;
        const entry: CalendarEntry = { id: `deadline-${deadline.id}`, type: 'deadline', date, title: deadline.designation, source: deadline, chipClass: 'entry-type-deadline', chipLabel: '' };
        entry.chipLabel = this.entryTypeLabel(entry);
        return entry;
      })
      .filter((entry): entry is CalendarEntry => entry !== null);

    this.allEntries = [...taskEntries, ...deadlineEntries];

    const tagsById = new Map<number, TaskTag>();
    taskEntries.forEach((entry) => ((entry.source as ProjectTaskListItem).tags ?? []).forEach((tag) => tagsById.set(tag.id, tag)));
    this.tagOptions = [...tagsById.values()].sort((a, b) => a.name.localeCompare(b.name));
    this.selectedTagIds = this.selectedTagIds.filter((id) => tagsById.has(id));
  }

  setTagFilter(tagIds: number[]): void {
    this.selectedTagIds = tagIds;
  }

  entryTags(entry: CalendarEntry): TaskTag[] {
    return entry.type === 'task' ? ((entry.source as ProjectTaskListItem).tags ?? []) : [];
  }

  setFilter(type: CalendarEntryType | 'all'): void {
    this.filterType = type;
  }

  goToPrevMonth(): void {
    this.setViewDate(new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1));
  }

  goToNextMonth(): void {
    this.setViewDate(new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1));
  }

  goToToday(): void {
    this.setViewDate(this.firstOfMonth(new Date()));
  }

  /** Sets the displayed month and remembers it (per project, sessionStorage — same mechanism
   *  ContractService uses for setPreviousUrl()) so it survives navigating away and back, e.g.
   *  via "Open task"/"Open contract", which destroy and recreate this component. Public: also
   *  called directly from the template for CalendarMonthPanelComponent's (monthPicked) output. */
  setViewDate(date: Date): void {
    this.viewDate = date;
    sessionStorage.setItem(this.viewDateStorageKey, `${date.getFullYear()}-${date.getMonth()}`);
  }

  private get viewDateStorageKey(): string {
    return `calendarViewMonth_${this.projectId}`;
  }

  /** Reads back whichever month was last saved for this project, falling back to the current
   *  month if nothing was saved yet (first visit) or the stored value is malformed. */
  private restoreViewDate(): Date {
    const saved = sessionStorage.getItem(this.viewDateStorageKey);
    const match = saved ? /^(\d{4})-(\d{1,2})$/.exec(saved) : null;
    if (!match) return this.firstOfMonth(new Date());
    const [, year, month] = match;
    return new Date(Number(year), Number(month), 1);
  }

  get monthLabel(): string {
    return new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric' }).format(this.viewDate);
  }

  /** Every entry matching the active filter, sorted chronologically — the sidebar list is one
   *  flat list regardless of the displayed month (no more "Past months"/"This month"/"Next
   *  months" grouping); the displayed month only drives the calendarDays grid. */
  get sortedEntries(): CalendarEntry[] {
    return this.filteredEntries
      .slice()
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  /** Full grid of day cells (including leading/trailing days of adjacent months) for the displayed month. */
  get calendarDays(): CalendarDay[] {
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();

    const firstOfMonth = new Date(year, month, 1);
    const firstWeekdayIndex = (firstOfMonth.getDay() + 6) % 7; // 0 = Monday
    const gridStart = new Date(year, month, 1 - firstWeekdayIndex);

    const lastOfMonth = new Date(year, month + 1, 0);
    const lastWeekdayIndex = (lastOfMonth.getDay() + 6) % 7;
    const gridEnd = new Date(year, month, lastOfMonth.getDate() + (6 - lastWeekdayIndex));

    // Read per render, not once at construction: a session left open across midnight would
    // otherwise keep highlighting yesterday (and, after the "Today" button jumps to the new
    // month, highlight nothing at all).
    const today = new Date();

    const days: CalendarDay[] = [];
    for (let date = new Date(gridStart); date <= gridEnd; date.setDate(date.getDate() + 1)) {
      const cellDate = new Date(date);
      days.push({
        date: cellDate,
        inCurrentMonth: cellDate.getMonth() === month,
        isToday: this.isSameDate(cellDate, today),
        entries: this.filteredEntries.filter((entry) => this.isSameDate(entry.date, cellDate)),
      });
    }
    return days;
  }

  dayNumber(date: Date): string {
    return date.getDate().toString().padStart(2, '0');
  }

  monthAbbreviation(date: Date): string {
    return new Intl.DateTimeFormat(this.locale, { month: 'short' }).format(date).replace('.', '');
  }

  /** For deadlines, shows the specific Fristart (e.g. "Ausübungsfrist") when one is set,
   *  falling back to the generic "Vertragsfrist" label — tasks always show "Aufgabe". */
  entryTypeLabel(entry: CalendarEntry): string {
    if (entry.type === 'task') return this.labelTask;
    const deadlineType = this.contractService.getContractDeadlineTypeById(entry.source?.deadlineTypeId);
    return deadlineType || this.labelDeadline;
  }

  /** Opens the detail side panel for a task or deadline entry (#4549). Also closes the
   *  day-agenda panel: both dock to the same side, and the grid stays clickable while the
   *  agenda panel is open (it only covers part of the width), so an entry can be opened
   *  straight from a day cell's visible chips while the agenda panel is still up — without
   *  this, both would end up "open" at once, and closing the entry panel would reveal the
   *  agenda panel still sitting underneath it. */
  openEntry(entry: CalendarEntry): void {
    this.selectedEntry = entry;
    this.selectedOverflowDay = null;
    // Jump the grid to the entry's month. The sidebar list spans every month, so picking an
    // entry from it used to open the detail panel while the grid stayed where it was, with
    // nothing highlighted. Guarded on the month actually differing: the grid's leading/trailing
    // cells carry entries of the adjacent months too (calendarDays below builds them from the
    // unfiltered entry list), so an unguarded jump would reflow the whole grid out from under
    // the cursor when one of those chips is clicked. Assigns viewDate directly rather than
    // going through setViewDate() so that merely previewing a far-off entry doesn't overwrite
    // the remembered "where the user left off" month — only deliberate navigation (arrows,
    // month picker, Today) should move that anchor.
    if (entry.date.getFullYear() !== this.viewDate.getFullYear()
      || entry.date.getMonth() !== this.viewDate.getMonth()) {
      this.viewDate = this.firstOfMonth(entry.date);
    }
  }

  closeDetailPanel(): void {
    this.selectedEntry = null;
  }

  /** "Aufgabe öffnen" (#4550) — navigates to the task's full record in the project tasks tab. */
  openTaskRecord(): void {
    if (this.selectedEntry?.type !== 'task' || this.siteId == null) return;
    // ProjectTasksComponent matches the :taskId route param against each task's numeric `id`
    // (see its search()/ngOnInit route-param handling) to filter the list down to just this
    // task — `source.taskId` is a different field (a task code/number string), passing that
    // instead would silently fail to match anything and show the unfiltered list.
    const taskId = this.selectedEntry.source?.id;
    this.router.navigate(['/site', this.siteId, 'project', this.projectId, 'tasks', taskId]);
  }

  /**
   * "Vertrag öffnen" (#4550) — navigates to the contract's full record.
   * setPreviousUrl() is required here: the contract form's "Abbrechen" button navigates back
   * to whatever ContractService.getPreviousUrl() last stored (sessionStorage), so without
   * this call it would fall back to a stale value from an earlier, unrelated navigation
   * instead of returning here to the calendar.
   */
  openContractRecord(): void {
    if (this.selectedEntry?.type !== 'deadline') return;
    const contractId = this.selectedEntry.source?.contractId;
    if (contractId == null) return;
    this.contractService.setPreviousUrl(this.router.url);
    this.router.navigate(['/contracts/edit', contractId]);
  }

  /** Opens the day-agenda side panel listing every entry for that day ("+N" click, propagation
   *  already stopped by CalendarMonthPanelComponent before it emits). Also closes any open entry
   *  detail panel — see openEntry()'s comment for why both need to clear each other out rather
   *  than just relying on whichever click opened the other. */
  openDayAgenda(day: CalendarDay): void {
    this.selectedOverflowDay = day;
    this.selectedEntry = null;
  }

  closeDayAgenda(): void {
    this.selectedOverflowDay = null;
  }

  trackByEntryId(_index: number, entry: CalendarEntry): string {
    return entry.id;
  }

  private get filteredEntries(): CalendarEntry[] {
    const selectedTags = new Set(this.selectedTagIds);
    return this.allEntries.filter(
      (entry) =>
        (this.filterType === 'all' || entry.type === this.filterType) &&
        (selectedTags.size === 0 || this.entryTags(entry).some((tag) => selectedTags.has(tag.id)))
    );
  }

  private isSameDate(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  private firstOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  /** Parses a "yyyy-MM-dd"-style date-only value without UTC/local timezone drift. */
  private parseDateOnly(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    if (!match) return null;
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
}
